"use client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import Link from "next/link";

export function AdminCard({ children, className, ...props }) {
  return (
    <Card className={cn("bg-white dark:bg-gray-800 shadow-sm", className)} {...props}>
      <CardContent className="p-6">{children}</CardContent>
    </Card>
  );
}

export function AdminButton({ children, variant = "default", className, ...props }) {
  const variants = {
    default: "bg-blue-600 hover:bg-blue-700 text-white",
    secondary: "bg-gray-200 hover:bg-gray-300 text-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white",
    danger: "bg-red-600 hover:bg-red-700 text-white",
  };

  return (
    <Button
      className={cn(variants[variant], className)}
      {...props}
    >
      {children}
    </Button>
  );
}

export function AdminInput({ label, error, className, ...props }) {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <Input
        className={cn(
          "w-full",
          error && "border-red-500 focus-visible:ring-red-500",
          className
        )}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

export function AdminTextArea({ label, error, className, ...props }) {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <Textarea
        className={cn(
          "w-full",
          error && "border-red-500 focus-visible:ring-red-500",
          className
        )}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

export function AdminSelect({ label, value, onChange, options, error, className = "" }) {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={onChange}
        className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm ${className}`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

export function AdminLoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
}

export function AdminEmptyState({ title, description, actionText, onAction }) {
  return (
    <div className="text-center py-12">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {description}
      </p>
      <AdminButton onClick={onAction}>{actionText}</AdminButton>
    </div>
  );
}

export function AdminPageHeader({ title, description }) {
  return (
    <div className="space-y-1">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
        {title}
      </h1>
      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {description}
        </p>
      )}
    </div>
  );
}

export function AdminStatsCard({ title, count, description, href, icon, maxCount = 100 }) {
  const progress = Math.min((count / maxCount) * 100, 100);
  
  return (
    <Link href={href}>
      <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow hover:shadow-lg transition-all duration-200 cursor-pointer dark:bg-gray-800 sm:px-6 sm:pt-6">
        <dt>
          <div className="absolute rounded-md bg-blue-500 p-3">
            <div className="h-6 w-6 text-white" aria-hidden="true">{icon}</div>
          </div>
          <p className="ml-16 truncate text-sm font-medium text-gray-500 dark:text-gray-400">
            {title}
          </p>
        </dt>
        <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
          <div className="flex-1">
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">
              {count}
            </p>
            <div className="mt-2 h-2 w-full bg-gray-200 rounded-full overflow-hidden dark:bg-gray-700">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <p className="ml-4 text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </dd>
      </div>
    </Link>
  );
} 